// API Configuration
const API_BASE_URL: string = process.env.REACT_APP_API_URL || '/api';

// Token management (kept for backward compatibility, but cookies are primary)
const getToken = (): string | null => localStorage.getItem('access_token');

const setTokens = (access: string, refresh: string): void => {
  // Store in localStorage as backup (cookies are primary)
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
};

const clearTokens = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('username');
};

interface ApiError extends Error {
  status?: number;
  response?: {
    status: number;
    data: unknown;
  };
}

type ApiRequestOptions = RequestInit;

// API request helper
const apiRequest = async <T = unknown>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> => {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Include cookies for cross-origin requests
  });

  // Handle unauthorized - throw error but don't redirect (let AuthContext handle it)
  if (response.status === 401) {
    const error: ApiError = new Error('Unauthorized');
    error.status = 401;
    throw error;
  }

  const data: unknown = await response.json();

  if (!response.ok) {
    // Создаём ошибку с полной информацией об ответе
    const error: ApiError = new Error((data as { error?: string }).error || 'API Error');
    error.response = { status: response.status, data };
    throw error;
  }

  return data as T;
};

// ============ Auth API ============

export const authAPI = {
  // Регистрация
  register: async (username: string, email: string, password: string, captchaToken: string): Promise<unknown> => {
    return apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password, captcha_token: captchaToken }),
    });
  },

  // Логин (по имени пользователя или email)
  login: async (usernameOrEmail: string, password: string): Promise<unknown> => {
    // Логин НЕ должен включать Authorization header
    const token = getToken();
    void token; // keep for potential future logic, avoid unused var lint

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ username: usernameOrEmail, password }),
      credentials: 'include',
    });

    const data: unknown = await response.json();

    if (!response.ok) {
      const err: ApiError = new Error((data as { error?: string }).error || 'Login failed');
      err.response = { status: response.status, data };
      throw err;
    }

    const tokens = data as { access_token?: string; refresh_token?: string };

    // Сохраняем токены
    if (tokens.access_token && tokens.refresh_token) {
      setTokens(tokens.access_token, tokens.refresh_token);
      // eslint-disable-next-line no-console
      console.log('Tokens saved:', {
        access_token: tokens.access_token ? 'present' : 'missing',
        refresh_token: tokens.refresh_token ? 'present' : 'missing',
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn('Login response missing tokens:', data);
    }

    return data;
  },

  // Логаут
  logout: async (): Promise<void> => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } finally {
      clearTokens();
    }
  },

  // Подтверждение email по токену из письма
  verifyEmail: async (token: string): Promise<unknown> => {
    return apiRequest('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  // Подтверждение email по 6-значному коду
  verifyEmailByCode: async (code: string): Promise<unknown> => {
    return apiRequest('/auth/verify-email-by-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  // Повторная отправка письма с подтверждением email
  resendVerification: async (email: string): Promise<unknown> => {
    return apiRequest('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Запрос на сброс пароля (письмо с кнопкой "Сбросить пароль")
  forgotPassword: async (email: string): Promise<unknown> => {
    return apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Смена пароля по токену из письма
  resetPassword: async (token: string, password: string): Promise<unknown> => {
    return apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  // Отправка кода для привязки email к существующему аккаунту (авторизованный)
  sendEmailCode: async (email: string): Promise<unknown> => {
    return apiRequest('/auth/email/send-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Подтверждение кода для привязки email (авторизованный)
  verifyEmailCode: async (code: string): Promise<unknown> => {
    return apiRequest('/auth/email/verify-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  // Проверка авторизации через API
  checkAuth: async (): Promise<unknown | null> => {
    try {
      const token = getToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        const data: unknown = await response.json();
        return data;
      }
      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('checkAuth error:', error);
      return null;
    }
  },

  // Проверка авторизации (депрекейтед - используй checkAuth)
  isAuthenticated: (): boolean => {
    return !!getToken() || !!localStorage.getItem('username');
  },

  // Получение текущего токена
  getToken,

  // Очистка токенов
  clearTokens,

  // CLI авторизация
  authorizeCLI: async (deviceCode: string): Promise<unknown> => {
    return apiRequest('/auth/cli/authorize', {
      method: 'POST',
      body: JSON.stringify({ device_code: deviceCode }),
    });
  },
};

interface CreateTunnelPayload {
  name: string;
  game?: string;
  protocol: string;
  localPort: string | number;
  localIP?: string;
}

// ============ Tunnels API ============

export const tunnelsAPI = {
  // Получить список туннелей
  list: async (): Promise<unknown> => {
    return apiRequest('/v1/tunnels');
  },

  // Создать туннель
  create: async (tunnelData: CreateTunnelPayload): Promise<unknown> => {
    return apiRequest('/v1/tunnels', {
      method: 'POST',
      body: JSON.stringify({
        name: tunnelData.name,
        game: tunnelData.game || 'custom',
        protocol: tunnelData.protocol.toLowerCase().replace('+', '_'), // TCP+UDP -> tcp_udp
        local_port: parseInt(String(tunnelData.localPort), 10),
        local_ip: tunnelData.localIP || '',
      }),
    });
  },

  // Получить туннель по ID
  get: async (id: string): Promise<unknown> => {
    return apiRequest(`/v1/tunnels/${id}`);
  },

  // Удалить туннель
  delete: async (id: string): Promise<unknown> => {
    return apiRequest(`/v1/tunnels/${id}`, {
      method: 'DELETE',
    });
  },

  // Получить статистику туннеля
  getStats: async (id: string): Promise<unknown> => {
    return apiRequest(`/v1/tunnels/${id}/stats`);
  },

  // Получить активные подключения
  getConnections: async (id: string): Promise<unknown> => {
    return apiRequest(`/v1/tunnels/${id}/connections`);
  },
};

// ============ Billing API ============

export const billingAPI = {
  createPayment: async (): Promise<unknown> => {
    return apiRequest('/billing/create-payment', {
      method: 'POST',
    });
  },

  checkPaymentStatus: async (paymentId: string): Promise<unknown> => {
    return apiRequest(`/billing/check-payment/${paymentId}`, {
      method: 'POST',
    });
  },

  upgradeToPro: async (): Promise<unknown> => {
    return apiRequest('/billing/upgrade-pro', {
      method: 'POST',
    });
  },
};

// ============ Health API ============

export const healthAPI = {
  check: async (): Promise<unknown> => {
    return apiRequest('/health');
  },
};

// Export apiRequest for use in other modules
export { apiRequest };

// Export all
export default {
  auth: authAPI,
  tunnels: tunnelsAPI,
  billing: billingAPI,
  health: healthAPI,
};

